import { format, parse, isAfter, differenceInMinutes, isSameDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { supabase } from '@/integrations/supabase/client';
import { ShiftStatusService, createShiftStatusService } from '@/lib/scheduling/shift-status-service';
import { ShiftStatus, ALL_SHIFT_STATUSES } from '@/lib/scheduling/shift-status-types';
import { BusinessRulesManager, getBusinessRulesManager } from '@/lib/business/business-rules-config';


// Legacy constant kept for backward compatibility - now configurable per business
export const ATTENDANCE_RULES = {
  ON_TIME_GRACE_MINUTES: 5,
  TOO_EARLY_THRESHOLD_MINUTES: 30,
  EARLY_DEPARTURE_THRESHOLD_MINUTES: 15,
  STANDARD_WORK_DAY_HOURS: 8,
  OVERTIME_THRESHOLD_MINUTES: 480, // 8 hours
} as const;

// Core Types
export interface TimeLogRecord {
  id: string;
  business_id: string;
  employee_id: string;
  event_type: 'arrival' | 'departure';
  scanned_at: string;
  created_at: string;
}

export interface ShiftRecord {
  id: string;
  employee_id: string | null;
  start_time: string;
  end_time: string;
  title?: string;
  employees?: { id: string; name: string } | null;
}

export interface EmployeeAttendanceStatus {
  employeeId: string;
  employeeName: string;
  shiftId: string;
  scheduledStartTime: Date;
  scheduledEndTime: Date;
  actualArrivalTime: Date | null;
  actualDepartureTime: Date | null;
  status: ShiftStatus;
  lateMinutes?: number;
  minutesEarly?: number;
  hoursWorked: number;
  hoursScheduled: number;
  isOvertime: boolean;
  overtimeMinutes: number;
}

export interface DailyAttendanceSummary {
  date: Date;
  totalScheduledEmployees: number;
  totalPresentEmployees: number;
  attendanceRate: number;
  punctualityRate: number;
  statusCounts: Map<ShiftStatus, number>;
  timeMetrics: {
    totalScheduledMinutes: number;
    totalWorkedMinutes: number;
    totalOvertimeMinutes: number;
    utilizationRate: number;
  };
  employeeStatuses: EmployeeAttendanceStatus[];
}

export interface BusinessAttendanceMetrics {
  period: {
    start: Date;
    end: Date;
    type: 'day' | 'week' | 'month';
  };
  totalEmployees: number;
  activeEmployees: number;
  averageAttendanceRate: number;
  averagePunctualityRate: number;
  totalLateArrivals: number;
  totalEarlyDepartures: number;
  totalOvertimeHours: number;
  topPerformerEmployeeId: string | null;
  dailySummaries: DailyAttendanceSummary[];
}

/**
 * Core Attendance Service Class
 * Centralizes all attendance calculation logic and provides consistent APIs
 */
export class AttendanceService {
  private businessId: string;
  private rulesManager: BusinessRulesManager;
  private shiftStatusService: ShiftStatusService;

  constructor(businessId: string, customRulesManager?: BusinessRulesManager, businessTimezone?: string) {
    this.businessId = businessId;
    this.rulesManager = customRulesManager || getBusinessRulesManager(businessId);
    this.shiftStatusService = createShiftStatusService({}, businessTimezone || 'UTC');
  }

  /**
   * Fetch and process attendance data for a specific date
   */
  async getDailyAttendance(date: Date): Promise<DailyAttendanceSummary> {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    const dayStartISO = dayStart.toISOString();
    const dayEndISO = dayEnd.toISOString();

    // Fetch shifts for the day
    const { data: currentShifts, error: shiftsError } = await supabase
      .from('shifts')
      .select('id, start_time, end_time, title, employee_id, employees(id, name)')
      .eq('business_id', this.businessId)
      .gte('start_time', dayStartISO)
      .lte('end_time', dayEndISO);

    if (shiftsError) throw shiftsError;

    // Fetch historical shifts if any
    const { data: historicalShifts, error: historicalError } = await supabase
      .from('shifts_history')
      .select('id, start_time, end_time, title, employee_id, employees(id, name)')
      .eq('business_id', this.businessId)
      .gte('start_time', dayStartISO)
      .lte('end_time', dayEndISO);

    if (historicalError) throw historicalError;

    // Fetch time logs for the day
    const { data: timeLogs, error: timeLogsError } = await supabase
      .from('time_logs')
      .select('id, business_id, employee_id, event_type, scanned_at, created_at')
      .eq('business_id', this.businessId)
      .gte('scanned_at', dayStartISO)
      .lte('scanned_at', dayEndISO);

    if (timeLogsError) throw timeLogsError;

    // Combine and process shifts
    const allShifts = [...(currentShifts || []), ...(historicalShifts || [])];
    const employeeStatuses = this.processShiftsWithTimeLogs(allShifts, timeLogs || [], date);

    return this.calculateDailySummary(date, employeeStatuses);
  }

  /**
   * Get attendance metrics for a time period (week, month, etc.)
   */
  async getBusinessAttendanceMetrics(
    startDate: Date, 
    endDate: Date, 
    periodType: 'day' | 'week' | 'month' = 'week'
  ): Promise<BusinessAttendanceMetrics> {
    console.log('getBusinessAttendanceMetrics: Starting for business:', this.businessId, 'period:', startDate, 'to', endDate, 'type:', periodType);
    
    const dailySummaries: DailyAttendanceSummary[] = [];
    
    // Generate daily summaries for the period
    const currentDate = new Date(startDate);
    let dayCount = 0;
    
    while (currentDate <= endDate) {
      dayCount++;
      console.log('getBusinessAttendanceMetrics: Processing day', dayCount, ':', currentDate.toISOString());
      
      try {
        const dailySummary = await this.getDailyAttendance(new Date(currentDate));
        dailySummaries.push(dailySummary);
        console.log('getBusinessAttendanceMetrics: Day', dayCount, 'processed successfully');
      } catch (error) {
        console.error(`getBusinessAttendanceMetrics: Error getting attendance for ${currentDate.toISOString()}:`, error);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    console.log('getBusinessAttendanceMetrics: Finished processing', dayCount, 'days. Calculating business metrics...');
    
    try {
      const metrics = this.calculateBusinessMetrics(startDate, endDate, periodType, dailySummaries);
      console.log('getBusinessAttendanceMetrics: Business metrics calculated successfully:', metrics);
      return metrics;
    } catch (error) {
      console.error('getBusinessAttendanceMetrics: Error calculating business metrics:', error);
      throw error;
    }
  }

  /**
   * Get monthly attendance aggregated by employee
   * Used by usePlanFulfillment to calculate hours worked vs target
   */
  async getMonthlyAttendanceByEmployee(
    employeeIds: string[],
    monthStart: Date,
    monthEnd: Date
  ): Promise<Map<string, {
    hoursWorked: number;
    overtimeMinutes: number;
    shiftsCompleted: number;
    shiftsScheduled: number;
    hoursPlanned: number;
  }>> {
    if (!employeeIds.length) {
      return new Map();
    }

    const monthStartISO = monthStart.toISOString();
    const monthEndISO = monthEnd.toISOString();
    const now = new Date();

    // Fetch all shifts for the period
    const { data: shifts, error: shiftsError } = await supabase
      .from('shifts')
      .select('id, start_time, end_time, employee_id')
      .eq('business_id', this.businessId)
      .in('employee_id', employeeIds)
      .gte('start_time', monthStartISO)
      .lte('start_time', monthEndISO);

    if (shiftsError) throw shiftsError;

    // Fetch all time logs for the period
    const { data: timeLogs, error: timeLogsError } = await supabase
      .from('time_logs')
      .select('id, employee_id, event_type, scanned_at')
      .eq('business_id', this.businessId)
      .in('employee_id', employeeIds)
      .gte('scanned_at', monthStartISO)
      .lte('scanned_at', monthEndISO)
      .order('scanned_at', { ascending: true });

    if (timeLogsError) throw timeLogsError;

    // Initialize result map
    const resultMap = new Map<string, {
      hoursWorked: number;
      overtimeMinutes: number;
      shiftsCompleted: number;
      shiftsScheduled: number;
      hoursPlanned: number;
    }>();

    // Initialize all employees with zeros
    employeeIds.forEach(empId => {
      resultMap.set(empId, {
        hoursWorked: 0,
        overtimeMinutes: 0,
        shiftsCompleted: 0,
        shiftsScheduled: 0,
        hoursPlanned: 0,
      });
    });

    // Calculate hours worked from time logs (arrival/departure pairs)
    const logsByEmployee = new Map<string, typeof timeLogs>();
    (timeLogs || []).forEach(log => {
      if (!log.employee_id) return;
      const existing = logsByEmployee.get(log.employee_id) || [];
      existing.push(log);
      logsByEmployee.set(log.employee_id, existing);
    });

    const overtimeThresholdMinutes = this.rulesManager.getAttendanceRules().workingHours.overtimeThresholdMinutes;

    logsByEmployee.forEach((logs, empId) => {
      let totalMinutes = 0;
      let totalOvertimeMinutes = 0;
      let lastArrival: Date | null = null;

      // Sort by scanned_at
      const sortedLogs = logs.sort((a, b) =>
        new Date(a.scanned_at).getTime() - new Date(b.scanned_at).getTime()
      );

      for (const log of sortedLogs) {
        if (log.event_type === 'arrival') {
          lastArrival = new Date(log.scanned_at);
        } else if (log.event_type === 'departure' && lastArrival) {
          const departure = new Date(log.scanned_at);
          const minutes = differenceInMinutes(departure, lastArrival);
          if (minutes > 0 && minutes < 24 * 60) { // Sanity check: less than 24 hours
            totalMinutes += minutes;
            // Check overtime per session
            if (minutes > overtimeThresholdMinutes) {
              totalOvertimeMinutes += minutes - overtimeThresholdMinutes;
            }
          }
          lastArrival = null;
        }
      }

      // Handle in-progress (employee still working today)
      if (lastArrival && isSameDay(lastArrival, now)) {
        const minutes = differenceInMinutes(now, lastArrival);
        if (minutes > 0 && minutes < 24 * 60) {
          totalMinutes += minutes;
          if (minutes > overtimeThresholdMinutes) {
            totalOvertimeMinutes += minutes - overtimeThresholdMinutes;
          }
        }
      }

      const data = resultMap.get(empId);
      if (data) {
        data.hoursWorked = Math.round((totalMinutes / 60) * 10) / 10; // Round to 1 decimal
        data.overtimeMinutes = totalOvertimeMinutes;
      }
    });

    // Calculate shifts data
    (shifts || []).forEach(shift => {
      if (!shift.employee_id) return;
      const data = resultMap.get(shift.employee_id);
      if (!data) return;

      data.shiftsScheduled++;

      // Calculate planned hours from shift duration
      if (shift.start_time && shift.end_time) {
        const shiftStart = new Date(shift.start_time);
        const shiftEnd = new Date(shift.end_time);
        const shiftMinutes = differenceInMinutes(shiftEnd, shiftStart);
        if (shiftMinutes > 0 && shiftMinutes < 24 * 60) {
          data.hoursPlanned += shiftMinutes / 60;
        }
      }

      // Count as completed if shift end time is in the past
      if (shift.end_time && new Date(shift.end_time) < now) {
        data.shiftsCompleted++;
      }
    });

    // Round planned hours
    resultMap.forEach(data => {
      data.hoursPlanned = Math.round(data.hoursPlanned * 10) / 10;
    });

    return resultMap;
  }

  /**
   * Get current day's real-time attendance status (for dashboard)
   */
  async getCurrentDayStatus(): Promise<{
    loggedInEmployees: number;
    scheduledEmployees: number;
    statusText: string;
    dotColorClass: string;
    attendanceSummary: DailyAttendanceSummary;
  }> {
    const today = new Date();
    const attendanceSummary = await this.getDailyAttendance(today);
    
    const { totalScheduledEmployees, totalPresentEmployees, statusCounts } = attendanceSummary;
    const hasIssues = (statusCounts.get(ShiftStatus.LATE) || 0) > 0 || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0;
    
    let statusText = 'N/A';
    let dotColorClass = 'bg-gray-400';

    if (totalPresentEmployees === totalScheduledEmployees && !hasIssues) {
      statusText = 'Optimal';
      dotColorClass = 'bg-green-500';
    } else if (totalPresentEmployees < totalScheduledEmployees || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0) {
      statusText = 'Understaffed';
      dotColorClass = 'bg-red-500';
    } else if (hasIssues) {
      statusText = 'Issues Present';
      dotColorClass = 'bg-orange-500';
    } else if (totalPresentEmployees > totalScheduledEmployees) {
      statusText = 'Overstaffed';
      dotColorClass = 'bg-blue-500';
    } else {
      statusText = 'Good';
      dotColorClass = 'bg-green-500';
    }

    return {
      loggedInEmployees: totalPresentEmployees,
      scheduledEmployees: totalScheduledEmployees,
      statusText,
      dotColorClass,
      attendanceSummary
    };
  }

  /**
   * Process shifts with time logs to determine attendance status
   */
  private processShiftsWithTimeLogs(
    shifts: ShiftRecord[], 
    timeLogs: TimeLogRecord[], 
    referenceDate: Date
  ): EmployeeAttendanceStatus[] {
    const employeeStatuses: EmployeeAttendanceStatus[] = [];

    // Create maps for quick lookups
    const arrivalTimes = new Map<string, Date>();
    const departureTimes = new Map<string, Date>();

    timeLogs.forEach(log => {
      if (log.employee_id) {
        const logDate = new Date(log.scanned_at);
        if (log.event_type === 'arrival') {
          arrivalTimes.set(log.employee_id, logDate);
        } else if (log.event_type === 'departure') {
          departureTimes.set(log.employee_id, logDate);
        }
      }
    });

    shifts.forEach(shift => {
      if (!shift.employee_id) {
        // Handle unassigned shifts separately if needed
        return;
      }

      const scheduledStart = new Date(shift.start_time);
      const scheduledEnd = new Date(shift.end_time);
      const actualArrival = arrivalTimes.get(shift.employee_id);
      const actualDeparture = departureTimes.get(shift.employee_id);

      // Calculate attendance status using timezone-aware service
      const statusResult = this.shiftStatusService.calculateDetailedShiftStatus(
        scheduledStart,
        scheduledEnd,
        shift.employee_id, // employee is assigned
        actualArrival?.toISOString() || null,
        actualDeparture?.toISOString() || null
      );
      const status = statusResult.status;
      const lateMinutes = statusResult.lateMinutes;
      const minutesEarly = statusResult.minutesEarly;

      // Calculate hours worked and scheduled
      const hoursScheduled = differenceInMinutes(scheduledEnd, scheduledStart) / 60;
      let hoursWorked = 0;
      let overtimeMinutes = 0;

      if (actualArrival && actualDeparture) {
        const workedMinutes = differenceInMinutes(actualDeparture, actualArrival);
        hoursWorked = workedMinutes / 60;
        
        const overtimeThresholdMinutes = this.rulesManager.getAttendanceRules().workingHours.overtimeThresholdMinutes;
        if (workedMinutes > overtimeThresholdMinutes) {
          overtimeMinutes = workedMinutes - overtimeThresholdMinutes;
        }
      } else if (actualArrival && !actualDeparture && isSameDay(referenceDate, new Date())) {
        // Still working - calculate up to current time
        const now = new Date();
        const workedMinutes = differenceInMinutes(now, actualArrival);
        hoursWorked = workedMinutes / 60;
        
        const overtimeThresholdMinutes = this.rulesManager.getAttendanceRules().workingHours.overtimeThresholdMinutes;
        if (workedMinutes > overtimeThresholdMinutes) {
          overtimeMinutes = workedMinutes - overtimeThresholdMinutes;
        }
      }

      employeeStatuses.push({
        employeeId: shift.employee_id,
        employeeName: shift.employees?.name || 'Unknown',
        shiftId: shift.id,
        scheduledStartTime: scheduledStart,
        scheduledEndTime: scheduledEnd,
        actualArrivalTime: actualArrival || null,
        actualDepartureTime: actualDeparture || null,
        status,
        lateMinutes,
        minutesEarly,
        hoursWorked,
        hoursScheduled,
        isOvertime: overtimeMinutes > 0,
        overtimeMinutes
      });
    });

    return employeeStatuses;
  }

  /**
   * Calculate daily attendance summary from employee statuses
   */
  private calculateDailySummary(date: Date, employeeStatuses: EmployeeAttendanceStatus[]): DailyAttendanceSummary {
    const totalScheduledEmployees = employeeStatuses.length;
    const totalPresentEmployees = employeeStatuses.filter(emp => emp.actualArrivalTime).length;

    // Count statuses using unified ShiftStatus enum
    const statusCounts = new Map<ShiftStatus, number>();
    
    // Initialize all status counts to 0
    ALL_SHIFT_STATUSES.forEach(status => {
      statusCounts.set(status, 0);
    });
    
    // Count actual statuses from employee data
    employeeStatuses.forEach(emp => {
      const currentCount = statusCounts.get(emp.status) || 0;
      statusCounts.set(emp.status, currentCount + 1);
    });

    // Calculate time metrics
    const totalScheduledMinutes = employeeStatuses.reduce((sum, emp) => sum + (emp.hoursScheduled * 60), 0);
    const totalWorkedMinutes = employeeStatuses.reduce((sum, emp) => sum + (emp.hoursWorked * 60), 0);
    const totalOvertimeMinutes = employeeStatuses.reduce((sum, emp) => sum + emp.overtimeMinutes, 0);

    const attendanceRate = totalScheduledEmployees > 0 
      ? Math.round((totalPresentEmployees / totalScheduledEmployees) * 100) 
      : 100;

    const punctualityRate = totalScheduledEmployees > 0
      ? Math.round(((totalScheduledEmployees - (statusCounts.get(ShiftStatus.LATE) || 0)) / totalScheduledEmployees) * 100)
      : 100;

    const utilizationRate = totalScheduledMinutes > 0
      ? Math.round((totalWorkedMinutes / totalScheduledMinutes) * 100)
      : 0;

    return {
      date,
      totalScheduledEmployees,
      totalPresentEmployees,
      attendanceRate,
      punctualityRate,
      statusCounts,
      timeMetrics: {
        totalScheduledMinutes,
        totalWorkedMinutes,
        totalOvertimeMinutes,
        utilizationRate
      },
      employeeStatuses
    };
  }

  /**
   * Calculate business-wide attendance metrics from daily summaries
   */
  private calculateBusinessMetrics(
    startDate: Date, 
    endDate: Date, 
    periodType: 'day' | 'week' | 'month',
    dailySummaries: DailyAttendanceSummary[]
  ): BusinessAttendanceMetrics {
    const allEmployees = new Set<string>();
    const activeEmployees = new Set<string>();
    let totalLateArrivals = 0;
    let totalEarlyDepartures = 0;
    let totalOvertimeMinutes = 0;
    const employeeHours = new Map<string, number>();

    // Aggregate data from daily summaries
    dailySummaries.forEach(summary => {
      totalLateArrivals += summary.statusCounts.get(ShiftStatus.LATE) || 0;
      totalOvertimeMinutes += summary.timeMetrics.totalOvertimeMinutes;
      
      summary.employeeStatuses.forEach(employee => {
        allEmployees.add(employee.employeeId);
        
        if (employee.actualArrivalTime) {
          activeEmployees.add(employee.employeeId);
        }
        
        // Track total hours per employee for top performer calculation
        const currentHours = employeeHours.get(employee.employeeId) || 0;
        employeeHours.set(employee.employeeId, currentHours + employee.hoursWorked);

        // Count early departures
        if (employee.actualDepartureTime && employee.actualArrivalTime) {
          const earlyDepartureThreshold = new Date(
            employee.scheduledEndTime.getTime() - this.rulesManager.getAttendanceRules().gracePeriodsMinutes.earlyDepartureThreshold * 60000
          );
          if (employee.actualDepartureTime < earlyDepartureThreshold) {
            totalEarlyDepartures++;
          }
        }
      });
    });

    // Calculate averages
    const averageAttendanceRate = dailySummaries.length > 0
      ? Math.round(dailySummaries.reduce((sum, day) => sum + day.attendanceRate, 0) / dailySummaries.length)
      : 0;

    const averagePunctualityRate = dailySummaries.length > 0
      ? Math.round(dailySummaries.reduce((sum, day) => sum + day.punctualityRate, 0) / dailySummaries.length)
      : 0;

    // Find top performer (most hours worked)
    let topPerformerEmployeeId = null;
    let maxHours = 0;
    for (const [employeeId, hours] of employeeHours.entries()) {
      if (hours > maxHours) {
        maxHours = hours;
        topPerformerEmployeeId = employeeId;
      }
    }

    return {
      period: {
        start: startDate,
        end: endDate,
        type: periodType
      },
      totalEmployees: allEmployees.size,
      activeEmployees: activeEmployees.size,
      averageAttendanceRate,
      averagePunctualityRate,
      totalLateArrivals,
      totalEarlyDepartures,
      totalOvertimeHours: Math.round((totalOvertimeMinutes / 60) * 100) / 100,
      topPerformerEmployeeId,
      dailySummaries
    };
  }
}

/**
 * Factory function to create an AttendanceService instance
 */
export const createAttendanceService = (
  businessId: string,
  customRulesManager?: BusinessRulesManager,
  businessTimezone?: string
): AttendanceService => {
  console.log('createAttendanceService: Creating service for business:', businessId, 'timezone:', businessTimezone || 'UTC');
  try {
    const service = new AttendanceService(businessId, customRulesManager, businessTimezone);
    console.log('createAttendanceService: Service created successfully');
    return service;
  } catch (error) {
    console.error('createAttendanceService: Error creating service:', error);
    throw error;
  }
};

/**
 * Utility functions for common attendance calculations
 */
export const AttendanceUtils = {
  /**
   * Format hours as HH:MM
   */
  formatHoursMinutes: (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  },

  /**
   * Get attendance status color class using unified ShiftStatus
   */
  getStatusColorClass: (status: ShiftStatus): string => {
    switch (status) {
      case ShiftStatus.ON_TIME:
      case ShiftStatus.COMPLETED:
        return 'text-green-500';
      case ShiftStatus.LATE:
      case ShiftStatus.COMPLETED_LATE:
        return 'text-orange-500';
      case ShiftStatus.TOO_EARLY:
        return 'text-primary';
      case ShiftStatus.MISSED:
        return 'text-red-500';
      case ShiftStatus.PENDING:
        return 'text-blue-500';
      case ShiftStatus.IN_PROGRESS:
        return 'text-emerald-500';
      case ShiftStatus.UNASSIGNED:
        return 'text-orange-500';
      case ShiftStatus.LEFT_EARLY:
        return 'text-amber-500';
      default:
        return 'text-gray-500';
    }
  },

  /**
   * Get business status from attendance summary using unified ShiftStatus
   */
  getBusinessStatusFromSummary: (summary: DailyAttendanceSummary) => {
    const { totalScheduledEmployees, totalPresentEmployees, statusCounts } = summary;
    const hasIssues = (statusCounts.get(ShiftStatus.LATE) || 0) > 0 || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0;
    
    if (totalPresentEmployees === totalScheduledEmployees && !hasIssues) {
      return { text: 'Optimal', color: 'bg-green-500' };
    } else if (totalPresentEmployees < totalScheduledEmployees || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0) {
      return { text: 'Understaffed', color: 'bg-red-500' };
    } else if (hasIssues) {
      return { text: 'Issues Present', color: 'bg-orange-500' };
    } else if (totalPresentEmployees > totalScheduledEmployees) {
      return { text: 'Overstaffed', color: 'bg-blue-500' };
    } else {
      return { text: 'Good', color: 'bg-green-500' };
    }
  }
};